import { useEffect, useState } from "react";
import { LuPlus, LuX } from "react-icons/lu";
import { EditorDialog } from "./editorDialog";
import { useAvailableAttributeGroups } from "./useAvailableAttributeGroups";
import {
  adminAddItemGroupsToProduct,
  adminRemoveItemGroupFromProduct,
  adminRemoveItemFromProductGroup,
  getProduct,
} from "../../../../../api/products";
const groupId = (group) => group.masterItemGroupId || group.itemGroupId;
const signature = (groups) =>
  JSON.stringify(
    groups
      .map((g) => [groupId(g), (g.items || []).map((i) => i.itemId).sort()])
      .sort(),
  );
const requireOk = (response) => {
  if (response?.error !== false)
    throw Error(
      "No se pudieron guardar los atributos. Tus selecciones se conservan; vuelve a guardar.",
    );
  return response.data;
};
export const ReferenceAttributes = ({
  data,
  registerSave,
  onDirty,
  disabled,
}) => {
  const {
    groups: available,
    loading,
    error,
    retry,
  } = useAvailableAttributeGroups(data?.productId);
  const [groups, setGroups] = useState(data?.itemGroups || []),
    [open, setOpen] = useState(false),
    [search, setSearch] = useState(""),
    [picked, setPicked] = useState([]);
  const [initial, setInitial] = useState(signature(data?.itemGroups || []));
  const change = (value) => {
    setGroups(value);
    onDirty();
  };
  useEffect(
    () =>
      registerSave("attributes", async (productId) => {
        if (signature(groups) === initial) return;
        const fresh = requireOk(await getProduct(productId));
        for (const current of fresh.itemGroups || []) {
          const desired = groups.find((g) => groupId(g) === groupId(current));
          if (!desired || !desired.items?.length) {
            requireOk(
              await adminRemoveItemGroupFromProduct(
                productId,
                current.itemGroupId,
              ),
            );
            continue;
          }
          for (const item of current.items || [])
            if (!desired.items.some((i) => i.itemId === item.itemId))
              requireOk(
                await adminRemoveItemFromProductGroup(
                  productId,
                  current.itemGroupId,
                  item.itemId,
                ),
              );
        }
        for (const group of groups.filter((g) => g.items?.length))
          requireOk(
            await adminAddItemGroupsToProduct(productId, {
              itemGroups: [
                {
                  itemGroupId: groupId(group),
                  itemIds: group.items.map((i) => i.itemId),
                },
              ],
            }),
          );
        setInitial(signature(groups));
      }),
    [groups, initial, registerSave],
  );
  return (
    <section className="productFormCard referenceAttributes">
      <header>
        <h2>Agrupaciones de Atributos</h2>
        <p>
          Modificaciones adicionales del producto. Puedes añadir todos sus
          atributos o solo los que apliquen; no generan variantes.
        </p>
      </header>
      <div className="productCardFields">
        <button
          type="button"
          className="referenceOutline"
          disabled={disabled || loading}
          onClick={() => {
            setPicked([]);
            setSearch("");
            setOpen(true);
          }}
        >
          <LuPlus />
          Añadir agrupación
        </button>
        {error && (
          <div role="alert">
            <p>{error}</p>
            <button type="button" onClick={retry}>
              Reintentar
            </button>
          </div>
        )}
        {!groups.length && (
          <p className="referenceEmpty">Este producto no tiene atributos.</p>
        )}
        <div className="referenceAttributeGroups">
          {groups.map((group) => {
            const template =
              available.find((g) => g.itemGroupId === groupId(group)) || group;
            return (
              <section key={groupId(group)}>
                <header>
                  <div>
                    <strong>{group.name}</strong>
                    <small>
                      {group.items?.length || 0} de{" "}
                      {template.items?.length || 0} atributos en este producto
                    </small>
                  </div>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() =>
                      change(
                        groups.map((g) =>
                          g === group
                            ? {
                                ...g,
                                items: group.items?.length
                                  ? []
                                  : [...(template.items || [])],
                              }
                            : g,
                        ),
                      )
                    }
                  >
                    {group.items?.length ? "Quitar todos" : "Seleccionar todos"}
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Quitar agrupación ${group.name}`}
                    onClick={() => change(groups.filter((g) => g !== group))}
                  >
                    <LuX />
                    Quitar
                  </button>
                </header>
                <div>
                  {(template.items || []).map((item) => (
                    <label
                      aria-label={`Incluir ${item.name}`}
                      key={item.itemId}
                    >
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={(group.items || []).some(
                          (i) => i.itemId === item.itemId,
                        )}
                        onChange={() =>
                          change(
                            groups.map((g) =>
                              g === group
                                ? {
                                    ...g,
                                    items: g.items.some(
                                      (i) => i.itemId === item.itemId,
                                    )
                                      ? g.items.filter(
                                          (i) => i.itemId !== item.itemId,
                                        )
                                      : [...g.items, item],
                                  }
                                : g,
                            ),
                          )
                        }
                      />
                      <span>
                        {item.name}
                        <small>
                          +{" "}
                          {Number(item.price || 0).toLocaleString("es-ES", {
                            style: "currency",
                            currency: "EUR",
                          })}
                        </small>
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      {open && (
        <EditorDialog
          title="Añadir agrupación de atributos"
          description="Se eligen entre las agrupaciones existentes del negocio."
          onClose={() => setOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="referenceOutline"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="referencePrimary"
                disabled={!picked.length}
                onClick={() => {
                  change([
                    ...groups,
                    ...available
                      .filter((g) => picked.includes(g.itemGroupId))
                      .map((g) => ({ ...g, masterItemGroupId: g.itemGroupId })),
                  ]);
                  setOpen(false);
                }}
              >
                Añadir
              </button>
            </>
          }
        >
          <input
            type="search"
            aria-label="Buscar agrupaciones"
            placeholder="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="referencePickerOptions">
            {available
              .filter(
                (g) =>
                  g.items?.length &&
                  !groups.some((x) => groupId(x) === g.itemGroupId) &&
                  g.name
                    .toLocaleLowerCase("es")
                    .includes(search.toLocaleLowerCase("es")),
              )
              .map((g) => (
                <label
                  aria-label={g.name}
                  key={g.itemGroupId}
                  htmlFor={`attribute-pick-${g.itemGroupId}`}
                >
                  <input
                    id={`attribute-pick-${g.itemGroupId}`}
                    type="checkbox"
                    checked={picked.includes(g.itemGroupId)}
                    onChange={() =>
                      setPicked((ids) =>
                        ids.includes(g.itemGroupId)
                          ? ids.filter((id) => id !== g.itemGroupId)
                          : [...ids, g.itemGroupId],
                      )
                    }
                  />
                  <span>
                    <strong>{g.name}</strong>
                    <small>{g.items.map((i) => i.name).join(" · ")}</small>
                  </span>
                </label>
              ))}
          </div>
        </EditorDialog>
      )}
    </section>
  );
};
